import { useMemo, useState } from 'react'
import { Box, Chip, Popover, Tab, Tabs, Typography } from '@mui/material'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { approvedDisciplineCodesForPractitioner } from 'src/mentor/components/MentorEventsPage/components/eventsListing/formSections/EventTypeConfiguration'
import ConsultationInsights from './insights/ConsultationInsights'
import TherapyInsights from './insights/TherapyInsights'
import ScreeningInsights from './insights/ScreeningInsights'
import PatientInsights from './insights/PatientInsights'
import {
  DEFAULT_PERIOD,
  ReportTimePeriodSelector,
  monthPeriod,
  periodChipSx,
  previousPeriod,
  recentMonths,
  sameMonthPriorYears,
} from './insights/insightsCharts'

/**
 * Practitioner "My Reports" insights — distinct from the clinic-owner volume stats. One sub-tab per
 * role the practitioner holds (Consultations / Therapy / Screening) plus a cross-cutting Patients
 * tab, scoped to their OWN reports at this clinic location. Each sub-tab fetches lazily on first
 * activation. The panel itself does NOT scroll — it flows inside the page's single scroll; only its
 * sub-tabs + shared time-period selector stay pinned (sticky), anchored just below the fixed page
 * header via {@code stickyTop}.
 */
export default function PractitionerReportInsightsPanel({
  clinicBrandId,
  clinicLocationId,
  mentorProfileData,
  stickyTop = 0,
  // When the stats page hoists a single shared period selector, it passes the period here and the
  // panel's own selector is hidden (the period stays controlled from above, sticky across tabs).
  externalPeriod,
  // The sub-tabs default to the LOGGED-IN user's roles (mentor portal: the practitioner views
  // their own reports). Admin surfaces (support portal) view someone ELSE's reports and pass the
  // {isDoctor,isTherapist,isScreener} derived from the viewed practitioner's approved services.
  rolesOverride = null,
}) {
  const roles = useMemo(() => rolesOverride || getAllRolesInBoolean() || {}, [rolesOverride])
  const approvedDisciplineCodes = useMemo(
    () => approvedDisciplineCodesForPractitioner(mentorProfileData),
    [mentorProfileData],
  )

  const tabs = useMemo(() => {
    const list = []
    if (roles.isDoctor) list.push({ id: 'consultations', label: 'Consultations' })
    if (roles.isTherapist) list.push({ id: 'therapy', label: 'Therapy' })
    if (roles.isScreener) list.push({ id: 'screening', label: 'Screening' })
    // Patient stats apply whenever the practitioner authors any clinical report.
    if (list.length > 0) list.push({ id: 'patients', label: 'Patients' })
    return list
  }, [roles])

  const [activeId, setActiveId] = useState(tabs[0]?.id)
  const [internalPeriod, setInternalPeriod] = useState(DEFAULT_PERIOD)
  const period = externalPeriod || internalPeriod
  const { from, to } = period

  // Comparison controls: off by default. `compareKind` picks what the current period is measured
  // against — the immediately-preceding window, an explicitly picked month, or (for a whole month)
  // the same month across previous years as a growth trajectory.
  const [compareOn, setCompareOn] = useState(false)
  const [compareKind, setCompareKind] = useState('previous') // 'previous' | 'pick' | 'trajectory'
  const [pickedComparePeriod, setPickedComparePeriod] = useState(null)
  const [pickAnchor, setPickAnchor] = useState(null)

  const comparison = useMemo(() => {
    if (!compareOn) return null
    if (compareKind === 'trajectory' && period.kind === 'month') {
      return { mode: 'trajectory', otherPeriods: sameMonthPriorYears(period, 4) }
    }
    if (compareKind === 'pick' && pickedComparePeriod) {
      return { mode: 'pairwise', otherPeriods: [pickedComparePeriod] }
    }
    return { mode: 'pairwise', otherPeriods: [previousPeriod(period)] }
  }, [compareOn, compareKind, period, pickedComparePeriod])

  if (tabs.length === 0) return null

  const currentId = activeId && tabs.some((t) => t.id === activeId) ? activeId : tabs[0]?.id

  return (
    <Box sx={{ backgroundColor: '#fff' }}>
      {/* Sticky bar: role sub-tabs + the shared time-period selector (common across sub-tabs). */}
      <Box
        sx={{
          position: 'sticky',
          top: `${stickyTop}px`,
          zIndex: 4,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 1.5, md: 2 },
          pt: 1,
        }}
      >
        <Typography variant="overline" sx={{ color: '#418571', fontWeight: 700, letterSpacing: 1 }}>
          My Reports
        </Typography>
        {tabs.length > 1 ? (
          <Tabs
            value={currentId}
            onChange={(e, v) => setActiveId(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40, py: 0.5 },
              '& .Mui-selected': { color: '#418571' },
              '& .MuiTabs-indicator': { backgroundColor: '#418571', height: 3 },
            }}
          >
            {tabs.map((t) => (
              <Tab key={t.id} value={t.id} label={t.label} />
            ))}
          </Tabs>
        ) : null}
        {!externalPeriod ? (
          <Box sx={{ pt: 1.5, pb: 1 }}>
            <ReportTimePeriodSelector value={period} onChange={setInternalPeriod} />
          </Box>
        ) : null}

        {/* Comparison controls. */}
        <Box sx={{ pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Chip
            icon={<CompareArrowsIcon sx={{ fontSize: 16 }} />}
            label={compareOn ? 'Comparing' : 'Compare'}
            size="small"
            onClick={() => setCompareOn((v) => !v)}
            sx={periodChipSx(compareOn)}
          />
          {compareOn ? (
            <>
              <Chip
                label="vs previous"
                size="small"
                onClick={() => setCompareKind('previous')}
                sx={periodChipSx(compareKind === 'previous')}
              />
              {period.kind === 'month' ? (
                <Chip
                  label="vs previous years"
                  size="small"
                  onClick={() => setCompareKind('trajectory')}
                  sx={periodChipSx(compareKind === 'trajectory')}
                />
              ) : null}
              <Chip
                label={
                  compareKind === 'pick' && pickedComparePeriod ? `vs ${pickedComparePeriod.label}` : 'vs a month…'
                }
                size="small"
                onClick={(e) => setPickAnchor(e.currentTarget)}
                sx={periodChipSx(compareKind === 'pick')}
              />
            </>
          ) : null}
          <Popover
            open={Boolean(pickAnchor)}
            anchorEl={pickAnchor}
            onClose={() => setPickAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ p: 2, maxWidth: 320 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                Compare with month
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {recentMonths(18)
                  .filter((m) => m.format('YYYY-MM') !== period.monthAnchor)
                  .map((m) => {
                    const anchor = m.format('YYYY-MM')
                    const selected = compareKind === 'pick' && pickedComparePeriod?.monthAnchor === anchor
                    return (
                      <Chip
                        key={anchor}
                        label={m.format("MMM 'YY")}
                        size="small"
                        onClick={() => {
                          setPickedComparePeriod(monthPeriod(m))
                          setCompareKind('pick')
                          setPickAnchor(null)
                        }}
                        sx={periodChipSx(selected)}
                      />
                    )
                  })}
              </Box>
            </Box>
          </Popover>
        </Box>
      </Box>

      {/* Content — flows inside the page's single scroll. */}
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f5f5f5' }}>
        {tabs.map((t) => {
          const isActive = currentId === t.id
          return (
            <Box key={t.id} sx={{ display: isActive ? 'block' : 'none' }}>
              {t.id === 'consultations' && (
                <ConsultationInsights
                  active={isActive}
                  clinicBrandId={clinicBrandId}
                  clinicLocationId={clinicLocationId}
                  from={from}
                  to={to}
                  primaryPeriod={period}
                  comparison={comparison}
                />
              )}
              {t.id === 'therapy' && (
                <TherapyInsights
                  active={isActive}
                  clinicBrandId={clinicBrandId}
                  clinicLocationId={clinicLocationId}
                  from={from}
                  to={to}
                  approvedDisciplineCodes={approvedDisciplineCodes}
                  primaryPeriod={period}
                  comparison={comparison}
                />
              )}
              {t.id === 'screening' && (
                <ScreeningInsights
                  active={isActive}
                  clinicBrandId={clinicBrandId}
                  clinicLocationId={clinicLocationId}
                  from={from}
                  to={to}
                  primaryPeriod={period}
                  comparison={comparison}
                />
              )}
              {t.id === 'patients' && (
                <PatientInsights
                  active={isActive}
                  clinicBrandId={clinicBrandId}
                  clinicLocationId={clinicLocationId}
                  from={from}
                  to={to}
                  primaryPeriod={period}
                  comparison={comparison}
                />
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
